import React from 'react';
import Overlay from '../Overlay';
import Select from 'react-select';
import {
  getLabel,
  buildRow,
  getLabels,
  newGetLabel,
  getProductsLabel,
  formatCurrencyWithoutDecimals,
  showModalNoOutsideClick,
} from '../../util/FormatUtil';
import SystemAPI from '../../network/SystemAPI';
import Validator from '../../validation/Validator';
import { Validators } from '../../validation/Validators';
import { sweetalert } from '../../App';
import { AdminPages } from './AdminNav';
import { FacilityAPI } from '../../network/FacilityAPI';
import AdminAPI from '../../network/AdminAPI';
import ShopAPI from '../../network/ShopAPI';
import { Order } from '../../types/Order';
import { PaymentFormModal } from '../modals/PaymentFormModal';
import PaymentAPI from '../../network/PaymentAPI';

interface ProbioticsOrderPageState {
  order: Order;
  facilities: any[];
  showLoading?;
  acknowledged: boolean;
  isSubmitting: boolean;
  products: any[];
  cart: any[];
  cartTotal: number;
  states?;
  countries?;
  savedPaymentInfo?;
  paymentInfo: { formData; totalAmount; lineItems };
  showPaymentInfo?: boolean;
}

export default class ProbioticsOrderPage extends React.Component<any, ProbioticsOrderPageState> {
  private paymentFormRef: any;
  constructor(props) {
    super(props);
    this.state = {
      showLoading: false,
      facilities: [],
      products: [],
      order: {
        ID: null,
        ReqNum: null,
        TransactionID: null,
        TransactionStatus: null,
        FacilityID: null,
        ProductID: null,
        Quantity: 1,
        TotalPrice: null,
      },
      acknowledged: false,
      isSubmitting: false,
      cart: [],
      cartTotal: 0,
      states: [],
      countries: [],
      savedPaymentInfo: false,
      paymentInfo: { formData: null, totalAmount: 0, lineItems: null },
      showPaymentInfo: false,
    };
    this.paymentFormRef = React.createRef();
    this.submitOrder = this.submitOrder.bind(this);
  }

  loadScript(src: string) {
    var tag = document.createElement('script');
    tag.src = src;
    document.body.appendChild(tag);
  }

  componentDidMount(): void {
    document.title = 'Ora-Protek Order Page';
    this.setState({ showLoading: true }, async () => {
      const facilities = await AdminAPI.getFacilitiesForSubmissionForm();
      const products = await ShopAPI.getProducts();
      const states = await SystemAPI.getAllStates();
      const countries = await SystemAPI.getAllCountries();

      this.loadScript(process.env.REACT_APP_AUTHORIZE_NET_LOADSCRIPT);

      this.setState({
        facilities: facilities.authorizedFacilities,
        //@ts-ignore
        products: products,
        states: states,
        countries: countries,
        showLoading: false,
      });
    });
  }

  async submitOrder(response, totalPayment, taxes, addressObjForShop, shippingEmail, lineItems) {
    let paymentInfo = {
      formData: response,
      totalAmount: totalPayment,
      taxes: taxes,
      addressObjForShop: addressObjForShop,
      shippingEmail: shippingEmail,
      lineItems: lineItems,
      facilityID: this.state.order.FacilityID,
    };

    this.setState({ showLoading: true }, async () => {
      try {
        let result = await ShopAPI.orderSubmitted(paymentInfo);
        if (result.success) {
          sweetalert
            .fire({
              icon: 'success',
              title: '',
              text: 'Order submitted successfully',
            })
            .then(() => {
              this.clearForm();
            });
        } else {
          sweetalert.fire({ icon: 'error', title: '', text: result.reason });
          this.setState({ showLoading: false });
        }
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
      }
    });
  }

  clearForm() {
    this.setState({
      order: {
        ID: null,
        ReqNum: null,
        TransactionID: null,
        TransactionStatus: null,
        FacilityID: null,
        ProductID: null,
        Quantity: 1,
        TotalPrice: null,
      },
      acknowledged: false,
      showLoading: false,
      cart: [],
      cartTotal: 0,
    });
  }

  addToCart() {
    if (!this.state.order) {
      return sweetalert.fire({ icon: 'error', title: '', text: 'Order form must be completed' });
    }
    if (!this.state.order.FacilityID) {
      return sweetalert.fire({ icon: 'error', title: '', text: 'Facility must be selected' });
    }
    if (!this.state.order.ProductID) {
      return sweetalert.fire({ icon: 'error', title: '', text: 'Product must be selected' });
    }
    if (!this.state.order.Quantity || this.state.order.Quantity < 1) {
      return sweetalert.fire({ icon: 'error', title: '', text: 'Quantity must be greater than 0' });
    }

    let cart = JSON.parse(JSON.stringify(this.state.cart));

    // Check if product is already in cart
    if (cart.length > 0) {
      let productInCartIndex = cart.findIndex((f) => f.productID === this.state.order.ProductID);

      if (productInCartIndex >= 0) {
        // Product exists in cart, update quantity and totalPrice
        cart[productInCartIndex].quantity += this.state.order.Quantity;
        cart[productInCartIndex].totalPrice =
          cart[productInCartIndex].quantity *
          this.state.products.find((f) => f.ID === this.state.order.ProductID).Price;

        this.setState((prevState) => ({
          cart: cart,
          cartTotal: cart && cart.length > 0 ? cart.reduce((acc, curr) => acc + curr.totalPrice, 0) : 0,
          order: {
            ID: null,
            ReqNum: null,
            TransactionID: null,
            TransactionStatus: null,
            FacilityID: prevState.order.FacilityID,
            ProductID: null,
            Quantity: 1,
            TotalPrice: null,
          },
        }));
        return;
      }
    }

    cart.push({
      productID: this.state.order.ProductID,
      cost: this.state.products.find((f) => f.ID === this.state.order.ProductID).Price,
      quantity: this.state.order.Quantity,
      units: this.state.products.find((f) => f.ID === this.state.order.ProductID).Units,
      totalPrice:
        this.state.order.Quantity * this.state.products.find((f) => f.ID === this.state.order.ProductID).Price,
    });

    this.setState((prevState) => ({
      cart: cart,
      cartTotal: cart && cart.length > 0 ? cart.reduce((acc, curr) => acc + curr.totalPrice, 0) : 0,
      order: {
        ID: null,
        ReqNum: null,
        TransactionID: null,
        TransactionStatus: null,
        FacilityID: prevState.order.FacilityID,
        ProductID: null,
        Quantity: 1,
        TotalPrice: null,
      },
    }));
  }

  removeFromCart(productID) {
    let cart = JSON.parse(JSON.stringify(this.state.cart));
    let productInCartIndex = cart.findIndex((f) => f.productID === productID);

    if (productInCartIndex >= 0) {
      cart.splice(productInCartIndex, 1);
      // Recalculate the cart total
      const newCartTotal = cart.reduce((acc, curr) => acc + curr.totalPrice, 0);
      this.setState((prevState) => ({
        cart: cart,
        cartTotal: newCartTotal,
        order: {
          ID: null,
          ReqNum: null,
          TransactionID: null,
          TransactionStatus: null,
          FacilityID: prevState.order?.FacilityID ?? null,
          ProductID: null,
          Quantity: 1,
          TotalPrice: null,
        },
      }));
    }
  }

  refreshSelectedFacility() {
    // ANetCustomerProfile created for Facility via PaymentFormModal
    // reload facilities and selectedFacility passed into PaymentFormModal?
    AdminAPI.getFacilitiesForSubmissionForm().then((data) => {
      this.setState({
        facilities: data.authorizedFacilities,
      });
    });
  }

  async checkForFacilityBillToInfo(facilityID) {
    let facility = this.state.facilities.find((f) => f.ID === facilityID);
    if (!facility.ANETCustomerProfileID || !facility.ANETCustomerPaymentProfileID) {
      return sweetalert.fire({
        icon: 'error',
        title: '',
        text: 'Selected Facility has no billing information. Please visit Facility page.',
      });
    }

    this.setState({ showLoading: true });

    try {
      const customerProfile = await PaymentAPI.getCustomerProfile(
        facility.ANETCustomerProfileID,
        facility.ANETCustomerPaymentProfileID
      );
      // console.log('customerProfile', customerProfile)
    } catch (error) {
      return sweetalert.fire({
        icon: 'error',
        title: '',
        text: 'Selected Facility has no billing information. Please visit Facility page.',
      });
    } finally {
      this.setState({ showLoading: false });
    }
  }

  getQuanityString(cartItem) {
    if (cartItem.quantity && cartItem.quantity > 1) {
      if (cartItem.units === 'case') {
        return `cases (12 bottles)`;
      } else {
        return `${cartItem.units}`;
      }
    } else if (cartItem.quantity === 1) {
      if (cartItem.units === 'case') {
        return `case (12 bottles)`;
      } else {
        return `${cartItem.units}`;
      }
    } else {
      return cartItem.units;
    }
  }

  increaseQuantity(productID: string) {
    let cart = [...this.state.cart];
    const productIndex = cart.findIndex((item) => item.productID === productID);

    if (productIndex >= 0) {
      cart[productIndex].quantity += 1;
      cart[productIndex].totalPrice = cart[productIndex].quantity * cart[productIndex].cost;
      this.setState({
        cart: cart,
        cartTotal: cart.reduce((acc, curr) => acc + curr.totalPrice, 0),
      });
    }
  }

  decreaseQuantity(productID: string) {
    let cart = [...this.state.cart];
    const productIndex = cart.findIndex((item) => item.productID === productID);

    if (productIndex >= 0 && cart[productIndex].quantity > 1) {
      cart[productIndex].quantity -= 1;
      cart[productIndex].totalPrice = cart[productIndex].quantity * cart[productIndex].cost;
      this.setState({
        cart: cart,
        cartTotal: cart.reduce((acc, curr) => acc + curr.totalPrice, 0),
      });
    }
  }

  render() {
    return (
      <div className="container-fluid  min-vh-100 ">
        <Overlay show_loading={this.state.showLoading} />
        <PaymentFormModal
          ref={this.paymentFormRef}
          selectedServices={null}
          associatedPrices={null}
          cart={this.state.cart}
          cartTotal={this.state.cartTotal}
          handleProductPaymentInfo={this.submitOrder}
          selectedFacility={
            this.state.facilities && this.state.facilities.length > 0 && this.state.order && this.state.order.FacilityID
              ? this.state.facilities?.find((f) => {
                  return f.ID === this.state.order.FacilityID;
                })
              : null
          }
          refreshSelectedFacility={this.refreshSelectedFacility}
          states={this.state.states}
          countries={this.state.countries}
          allowDiscountEntry={false}
          fromWalkin={false}
          fromShop={true}
        />

        <div className={'row'}>
          <div className=" col-12 pt-2">
            <main id="main-content" tabIndex={-1} aria-label={'Ora-Protek Order Form'}>
              <div className="card mt-2">
                <div className="card-header verlag-bold">
                  <h4 className="m-0">{'Ora-Protek Order Form'}</h4>
                </div>
                <div className="card-body">
                  {buildRow(
                    'Facility',
                    <Select
                      key={'Facility'}
                      id={'Facility'}
                      isDisabled={this.state.cart && this.state.cart.length > 0}
                      isSearchable={true}
                      aria-label={'Facility'}
                      placeholder={<div className="accessibilityText">{'Please Select...'}</div>}
                      noOptionsMessage={() => 'No option'}
                      value={newGetLabel(this.state.order.FacilityID, this.state.facilities, 'facility')}
                      onChange={(e) => {
                        this.checkForFacilityBillToInfo(e.value);
                        this.setState((prevState) => ({
                          order: { ...prevState.order, FacilityID: e.value, ProductID: null, Quantity: 1 },
                        }));
                      }}
                      className={'state_select w-100'}
                      options={getLabels(this.state.facilities, 'facility')}
                    />
                  )}
                  {buildRow(
                    'Product',
                    <Select
                      key={'Product'}
                      id={'Product'}
                      isSearchable={true}
                      aria-label={'Product'}
                      placeholder={<div className="accessibilityText">{'Please Select...'}</div>}
                      noOptionsMessage={() => 'No option'}
                      value={newGetLabel(this.state.order.ProductID, this.state.products, 'products')}
                      onChange={(e) =>
                        this.setState((prevState) => ({
                          order: { ...prevState.order, ProductID: e.value, ServiceID: null, Quantity: 1 },
                        }))
                      }
                      className={'state_select w-100'}
                      options={getLabels(this.state.products, 'products')}
                    />
                  )}
                  {buildRow(
                    'Quantity',
                    <input
                      min={1}
                      className={'form-control'}
                      autoComplete={'off'}
                      type={'number'}
                      name={'Quantity'}
                      onChange={(e) =>
                        this.setState((prevState) => ({
                          order: {
                            ...prevState.order,
                            Quantity: Number(e.target.value),
                          },
                        }))
                      }
                      value={this.state.order && this.state.order.Quantity ? this.state.order.Quantity : null}
                    />
                  )}
                  {buildRow(
                    '',
                    <button
                      className={'form-control btn btn-success'}
                      style={{ backgroundColor: '#1E6110', color: 'white' }}
                      name={'Add to Cart'}
                      onClick={(e) => {
                        this.addToCart();
                      }}
                    >
                      Add to Cart
                    </button>
                  )}
                  <div className="card mb-2  mt-2">
                    <div className="card-header verlag-bold">
                      <h4 style={{ textAlign: 'center' }}>Cart</h4>
                    </div>
                    <div className="card-body">
                      {this.state.cart && this.state.cart.length > 0 ? (
                        <div className="row">
                          <div className="col-1 cart-item"></div>
                          <div className="col-3 cart-item">
                            <h4>Product</h4>
                          </div>
                          <div className="col-2  cart-item">
                            <h4>Cost</h4>
                          </div>
                          <div className="col-3  cart-item">
                            <h4>Quantity</h4>
                          </div>
                          <div className="col-2  cart-item">
                            <h4>Price</h4>
                          </div>
                          <div className="col-1 cart-item"></div>
                        </div>
                      ) : (
                        <h5 className="text-center mt-2">Your cart is empty</h5>
                      )}
                      {this.state.cart.map((cartItem, index) => (
                        <div className="row" key={index}>
                          <div className="col-1 cart-item pt-3 text-center font-weight-bold">
                            <p>{index + 1}</p>
                          </div>
                          <div className="col-3 cart-item pt-3">
                            <p>{getProductsLabel(cartItem.productID, this.state.products).label}</p>
                          </div>
                          <div className="col-2  cart-item pt-3">
                            <p>
                              ${cartItem.cost}/{cartItem.units}
                            </p>
                          </div>
                          <div className="col-3 cart-item pt-3">
                            <div className="d-flex justify-content-between">
                              <p className="mx-2 mb-0">
                                {cartItem.quantity} {this.getQuanityString(cartItem)}
                              </p>
                              <div className="d-flex">
                                <button
                                  className="btn btn-sm btn-outline-secondary mr-2"
                                  onClick={() => this.decreaseQuantity(cartItem.productID)}
                                >
                                  -
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary ml-2"
                                  onClick={() => this.increaseQuantity(cartItem.productID)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="col-2  cart-item pt-3">
                            <p>{formatCurrencyWithoutDecimals(cartItem.totalPrice)}</p>
                          </div>
                          <div
                            className="col-1 cart-item pt-2 text-center font-weight-bold"
                            style={{ color: 'darkred', fontSize: '1.5em' }}
                            onClick={() => this.removeFromCart(cartItem.productID)}
                          >
                            X
                          </div>
                        </div>
                      ))}
                      {this.state.cart && this.state.cart.length > 0 && (
                        <div className="row">
                          <div className="col-6"></div>
                          <div
                            className="col-3 py-3 font-weight-bold"
                            style={{
                              borderRight: '1px solid lightgray',
                              borderBottom: '1px solid lightgray',
                              borderLeft: '1px solid lightgray',
                              backgroundColor: '#F7F7F7',
                            }}
                          >
                            Total
                          </div>
                          <div
                            className="col-2 pt-3 font-weight-bold"
                            style={{
                              borderRight: '1px solid lightgray',
                              borderBottom: '1px solid lightgray',
                              backgroundColor: '#F7F7F7',
                            }}
                          >
                            {formatCurrencyWithoutDecimals(
                              this.state.cart.reduce((acc, curr) => acc + curr.totalPrice, 0)
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={'pr-3 mt-2 pl-3 '}
                    style={{
                      height: '3.5em',
                      border: '1px solid lightgrey',
                      borderRadius: 5,
                      backgroundColor: '#F7F7F7',
                    }}
                  >
                    <button
                      className={'btn immySubmitButtonOutline float-right my-2'}
                      onClick={() => {
                        if (!this.state.cart || this.state.cart.length === 0) {
                          return sweetalert.fire({ icon: 'error', title: '', text: 'Cart is empty' });
                        }
                        showModalNoOutsideClick('paymentForm');
                      }}
                      disabled={this.state.isSubmitting}
                    >
                      Checkout
                    </button>
                    <button
                      className={'btn immyClearButtonOutline float-left my-2'}
                      onClick={() => {
                        this.clearForm();
                      }}
                      disabled={this.state.isSubmitting}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }
}
